import {
  buildLossPayload,
  buildPanelsByIndexFromPanelShadingData,
  buildPerPanelShadingArrays,
  finiteNumberOrDefault,
  normalizeMonthlyLoss,
  normalizeMonthlySolarAccess
} from './production-payload-utils';

const asArray = (value) => (Array.isArray(value) ? value : []);

const parseValue = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const positiveNumber = (...values) => {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number) && number > 0) return number;
  }
  return null;
};

const positiveInteger = (value, fallback = 1) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return fallback;
  return Math.max(1, Math.trunc(number));
};

const normalizeSelectedPanelIndices = (values) =>
  Array.from(
    new Set(
      asArray(values)
        .map((value) => {
          if (typeof value === 'number') return value;
          if (typeof value === 'string' && value.trim() !== '') {
            return Number(value);
          }
          return Number.NaN;
        })
        .filter(
          (value) =>
            Number.isFinite(value) && Number.isInteger(value) && value >= 0
        )
    )
  );

const inverterType = (value) => {
  const type = String(value || 'C')
    .trim()
    .toUpperCase();
  if (type === 'M' || type === 'MICRO' || type === 'MICROINVERTER') {
    return 'M';
  }
  if (
    type === 'P' ||
    type === 'POWEROPTIMIZER' ||
    type === 'POWER OPTIMIZER' ||
    type === 'POWER_OPTIMIZER'
  ) {
    return 'P';
  }
  return 'C';
};

const buildInverterHardware = (inverter, quantity, automatic) => {
  if (!inverter || typeof inverter !== 'object') return null;
  const type = inverterType(inverter.type);
  const id =
    inverter.id ??
    inverter.inverter_id ??
    inverter.inverterId ??
    inverter.companyInverterID ??
    inverter.companyInverterId ??
    '';
  const payload = {
    id,
    model: inverter.model || '',
    type,
    capacity: positiveNumber(inverter.capacity) || 6000,
    efficiency: inverter.efficiency || '96.00',
    max_dc_voltage: inverter.max_dc_voltage || '60.00',
    max_dc_current: inverter.max_dc_current || '12.00',
    min_mppt_voltage: inverter.min_mppt_voltage || '22.00',
    mppt_count_per_inverter: inverter.mppt_count_per_inverter || 1,
    strings_per_mppt_max: inverter.strings_per_mppt_max || 2,
    manufacturer_id:
      inverter.manufacturer?.name || inverter.manufacturer_id || 'Unknown'
  };
  if (!automatic) {
    const exactCount = positiveInteger(quantity);
    payload.exact_count = exactCount;
    payload.max_count = exactCount;
  }
  return payload;
};

const buildPanelHardware = (panel = {}) => ({
  id: panel.id || '',
  model: panel.model || '',
  name: panel.model || panel.name || '',
  series: panel.series || '',
  power: panel.power || 405,
  length: panel.length || 1722,
  width: panel.width || 1134,
  degradation: panel.degradation || '0.25',
  first_year_degradation: panel.first_year_degradation || '2.00',
  efficiency: panel.efficiency || '20.74',
  has_micro: panel.has_micro || false,
  v_oc: panel.v_oc || '37.23',
  temp_coeff_v_oc: panel.temp_coeff_v_oc || '-0.102',
  v_mpp: panel.v_mpp || '31.21',
  temp_coeff_p_mpp: panel.temp_coeff_p_mpp || '-0.350',
  i_sc: panel.i_sc || '13.87',
  temp_coeff_i_sc: panel.temp_coeff_i_sc || '0.040',
  installation_method: panel.installation_method || 'Rack-Mounted',
  manufacturer_id:
    panel.manufacturer?.name || panel.manufacturer_id || 'Unknown'
});

const defaultPowerOptimizer = () => ({
  id: 'default',
  company_id: 'default',
  status: 'active',
  display_name: 'Default Power Optimizer',
  manufacturer: 'Default',
  Efficiency: 99.5,
  Voc_Max: 60,
  Isc_Max: 15
});

export function buildProductionJson(input = {}) {
  const packageData = parseValue(input.packageData) || {};
  const panel = input.panel || packageData.panel || {};
  const segments = asArray(input.segments);
  const panelDataByIndex = asArray(input.panelDataByIndex);
  const selectedByVertex = input.selectedByVertex || {};
  const selectedPanelsByRoofId = input.selectedPanelsByRoofId || {};
  const selectedPanelIndexMap = [];
  const roofIndexMap = [];
  const roofs = [];

  const orderedSegments = segments
    .map((rawSegment, segmentIndex) => ({
      segment: parseValue(rawSegment) || {},
      segmentIndex
    }))
    .sort((left, right) => {
      const leftTsrf = finiteNumberOrDefault(
        left.segment.annualTSRF ??
          left.segment.annual_tsrf ??
          left.segment.TSRF?.[12] ??
          left.segment.tsrf?.[12],
        0
      );
      const rightTsrf = finiteNumberOrDefault(
        right.segment.annualTSRF ??
          right.segment.annual_tsrf ??
          right.segment.TSRF?.[12] ??
          right.segment.tsrf?.[12],
        0
      );
      const byTsrf = rightTsrf - leftTsrf;
      if (byTsrf !== 0) return byTsrf;
      const leftId = String(left.segment.id ?? left.segmentIndex);
      const rightId = String(right.segment.id ?? right.segmentIndex);
      return (
        leftId.localeCompare(rightId) || left.segmentIndex - right.segmentIndex
      );
    });

  orderedSegments.forEach(({ segment, segmentIndex }) => {
    const roofId = segment.id ?? String(segmentIndex);
    const panelData = parseValue(panelDataByIndex[segmentIndex]) || {};
    const points3D = asArray(panelData.points3D);
    const realPanels = panelData.realPanels;
    const hasRealPanels =
      (Array.isArray(realPanels) && realPanels.length > 0) ||
      Boolean(realPanels && typeof realPanels === 'object');
    const selectedRaw =
      selectedByVertex[segmentIndex] ??
      selectedByVertex[String(segmentIndex)] ??
      [];
    const selectedSet = new Set(normalizeSelectedPanelIndices(selectedRaw));
    const selectionByRoof = asArray(selectedPanelsByRoofId[roofId]);
    const panelIndices = points3D
      .map((_, index) => index)
      .filter((index) => !hasRealPanels || Boolean(realPanels[index]))
      .filter((index) => {
        if (!input.onlySelectedPanels) return true;
        if (selectedSet.size) return selectedSet.has(index);
        return (
          selectionByRoof.length === points3D.length &&
          Number(selectionByRoof[index]) > 0
        );
      });
    if (!panelIndices.length) return;

    const payloadRoofIndex = roofs.length;
    roofIndexMap.push(segmentIndex);
    selectedPanelIndexMap[payloadRoofIndex] = panelIndices;
    const monthlySolarAccess = normalizeMonthlySolarAccess(
      segment.solarAccess ?? segment.solar_access
    );
    const monthlyTof = normalizeMonthlyLoss(segment.TOF ?? segment.tof);
    const monthlyTsrf = normalizeMonthlyLoss(segment.TSRF ?? segment.tsrf);
    const annualSolarAccess = finiteNumberOrDefault(
      segment.annualAccess ??
        segment.annual_solar_access ??
        segment.solarAccess?.[12] ??
        segment.solar_access?.[12] ??
        1,
      1
    );
    const annualTof = finiteNumberOrDefault(
      segment.annualTOF ??
        segment.annual_tof ??
        segment.TOF?.[12] ??
        segment.tof?.[12] ??
        0,
      0
    );
    const annualTsrf = finiteNumberOrDefault(
      segment.annualTSRF ??
        segment.annual_tsrf ??
        segment.TSRF?.[12] ??
        segment.tsrf?.[12] ??
        0,
      0
    );
    const panelsByIndex = buildPanelsByIndexFromPanelShadingData({
      panelShadingData: asArray(input.panelShadingData),
      segmentIndex
    });
    const shading = buildPerPanelShadingArrays({
      panelIndicesInPayload: panelIndices,
      panelCount: panelIndices.length,
      panelsByIndex,
      segmentMonthlySA: monthlySolarAccess,
      segmentMonthlyTOF: monthlyTof,
      segmentMonthlyTSRF: monthlyTsrf,
      segmentAnnualSA: annualSolarAccess,
      segmentAnnualTOF: annualTof,
      segmentAnnualTSRF: annualTsrf
    });
    const geometries = panelIndices
      .map((index) => points3D[index])
      .filter(Boolean)
      .map((geometry) => geometry.map((point) => [point.x, point.y, point.z]));
    roofs.push({
      id: roofId,
      pitch: segment.pitch || 0,
      azimuth: segment.azimuth || 0,
      monthly_solar_access: monthlySolarAccess,
      monthly_tof: monthlyTof,
      monthly_tsrf: monthlyTsrf,
      monthly_poa: segment.poaMonthly || [],
      annual_solar_access: annualSolarAccess,
      annual_tof: annualTof,
      annual_tsrf: annualTsrf,
      annual_poa: asArray(segment.poaMonthly).reduce(
        (total, value) => total + Number(value || 0),
        0
      ),
      panels: {
        width: panel.width || 1134,
        length: panel.length || 1722,
        panel_count: panelIndices.length,
        geometries,
        ...shading
      }
    });
  });

  const primaryInverter = input.inverter || packageData.inverter;
  const inverterQuantity = positiveInteger(
    input.inverterQuantity ??
      packageData.inverterQuantity ??
      packageData.inverter_quantity ??
      1
  );
  const inverterHardware = [];
  const primaryPayload = buildInverterHardware(
    primaryInverter,
    inverterQuantity,
    Boolean(input.primaryInverterAutoQuantity)
  );
  if (primaryPayload) inverterHardware.push(primaryPayload);
  const explicitAdditional = asArray(input.additionalInverters);
  const packageAdditional = asArray(packageData.additional_inverters);
  const additional = explicitAdditional.length
    ? explicitAdditional
    : packageAdditional
        .map((entry) => {
          const id = entry.id ?? entry.inverter_id;
          const hardware = asArray(input.allInverters).find(
            (item) => String(item?.id) === String(id)
          );
          return hardware ? { ...hardware, ...entry, id: hardware.id } : entry;
        })
        .filter(Boolean);
  additional.forEach((item) => {
    const payload = buildInverterHardware(
      item,
      item.quantity ?? item.max_count ?? item.maxCount ?? 1,
      item.autoQuantity !== false && item.auto_quantity !== false
    );
    if (payload) inverterHardware.push(payload);
  });
  const usedInverters = asArray(input.invertersUsed);
  if (usedInverters.length) {
    const savedHardware = asArray(input.savedInverterHardware);
    const matchHardware = (item, list) => {
      const id = item?.hardware_id ?? item?.id ?? item?.inverter_id;
      const model = item?.model;
      return asArray(list).find(
        (entry) =>
          (id &&
            (String(entry?.id) === String(id) ||
              String(entry?.hardware_id) === String(id))) ||
          (model && String(entry?.model) === String(model))
      );
    };
    const locked = usedInverters
      .map((item) => {
        const saved = matchHardware(item, savedHardware);
        const catalog = matchHardware(item, input.allInverters);
        const source = saved || (item?.capacity ? item : null) || catalog;
        if (!source) return null;
        return buildInverterHardware(
          {
            ...catalog,
            ...source,
            id: source.id || catalog?.id || item.hardware_id || item.id
          },
          item.count ?? item.quantity ?? source.exact_count ?? 1,
          false
        );
      })
      .filter(Boolean);
    if (locked.length === usedInverters.length) {
      inverterHardware.splice(0, inverterHardware.length, ...locked);
    }
  }

  const wiring = input.wiring || input.wiringConfig || {};
  const defaultWiring = { ohm_per_m_string: 0.004, loop_factor: 2 };
  const wiringMatchesDefaults =
    Number(wiring.ohm_per_m_string) === defaultWiring.ohm_per_m_string &&
    Number(wiring.loop_factor) === defaultWiring.loop_factor;
  const preferStateWiring =
    Boolean(input.wiringConfigured) || !wiringMatchesDefaults;
  const wiringOhmPerMeter = preferStateWiring
    ? positiveNumber(wiring.ohm_per_m_string, primaryInverter?.ohm_per_m_string)
    : positiveNumber(
        primaryInverter?.ohm_per_m_string,
        wiring.ohm_per_m_string
      );
  const wiringLoopFactor = preferStateWiring
    ? positiveNumber(wiring.loop_factor, primaryInverter?.loop_factor)
    : positiveNumber(primaryInverter?.loop_factor, wiring.loop_factor);
  const powerOptimizer =
    input.powerOptimizer || packageData.powerOptimizer || null;
  const productionJson = {
    lat: input.lat,
    long: input.lon,
    roofs,
    inverter_hardware: inverterHardware,
    power_optimizer:
      powerOptimizer &&
      typeof powerOptimizer === 'object' &&
      Object.keys(powerOptimizer).length
        ? powerOptimizer
        : defaultPowerOptimizer(),
    panel_hardware: buildPanelHardware(panel),
    wiring: {
      ohm_per_m_string: wiringOhmPerMeter || defaultWiring.ohm_per_m_string,
      loop_factor: wiringLoopFactor || defaultWiring.loop_factor
    },
    loss: buildLossPayload(input.losses || packageData.losses || {}, {
      Availability: 1
    })
  };
  if (input.includeHourlyProduction) {
    productionJson.return_hourly_production = true;
  }
  if (input.includeDcacTarget) {
    productionJson.dcac_target =
      positiveNumber(
        input.dcac_target,
        input.maxDcAcRatio,
        input.designData?.maxDcAcRatio,
        input.designData?.dcac_target
      ) || 1.5;
  }
  return {
    productionJson,
    selectedPanelIndexMap,
    roofIndexMap
  };
}
